import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';
import { Send, Star, Bot, User, Loader2, Plus, MessageSquare, Trash2, Clock, PanelLeftClose, PanelLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatPage() {
  const { language, t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [ratingMsg, setRatingMsg] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/sessions`, { headers: getAuthHeaders(), withCredentials: true });
      setSessions(res.data);
    } catch {}
    setSessionsLoading(false);
  }, [getAuthHeaders]);

  // Load sessions on mount, then auto-load last conversation
  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`${API}/chat/sessions`, { headers: getAuthHeaders(), withCredentials: true });
        setSessions(res.data);
        if (res.data.length > 0) {
          loadSession(res.data[0].session_id);
        } else {
          startNewChat();
        }
      } catch {
        startNewChat();
      }
      setSessionsLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSession = async (sid) => {
    setSessionId(sid);
    try {
      const res = await axios.get(`${API}/chat/history?session_id=${sid}`, { headers: getAuthHeaders(), withCredentials: true });
      const loaded = [];
      res.data.forEach((m) => {
        loaded.push({ role: 'user', content: m.user_message });
        loaded.push({ role: 'assistant', content: m.ai_response, id: m.message_id });
      });
      setMessages(loaded);
    } catch {
      setMessages([{ role: 'assistant', content: t('chat_welcome'), id: 'welcome' }]);
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([{ role: 'assistant', content: t('chat_welcome'), id: 'welcome' }]);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/chat/sessions/${sid}`, { headers: getAuthHeaders(), withCredentials: true });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
    } catch {}
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setSending(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMsg,
        session_id: sessionId
      }, { headers: getAuthHeaders(), withCredentials: true });

      const newSid = res.data.session_id;
      if (!sessionId) setSessionId(newSid);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        id: res.data.message_id
      }]);
      // Refresh sessions list
      fetchSessions();
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: language === 'fr'
          ? 'Désolé, une erreur est survenue. Veuillez réessayer.'
          : 'Sorry, an error occurred. Please try again.',
        id: 'error'
      }]);
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleRate = async (messageId, rating) => {
    try {
      await axios.post(`${API}/chat/rate`, { message_id: messageId, rating }, { headers: getAuthHeaders(), withCredentials: true });
      setRatingMsg(null);
    } catch {}
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return language === 'fr' ? "Aujourd'hui" : 'Today';
    if (diff < 172800000) return language === 'fr' ? 'Hier' : 'Yesterday';
    return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="h-[calc(100vh-64px)] flex" data-testid="chat-page">
      <SEO
        title={language === 'fr' ? 'Assistant IA' : 'AI Assistant'}
        description="Discutez avec l'assistant IA Claude intégré à IA Academy"
        path="/chat"
        noIndex
      />

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-72' : 'w-0'} transition-all duration-300 overflow-hidden bg-slate-900 flex flex-col`} data-testid="chat-sidebar">
        <div className="p-3 border-b border-slate-700">
          <Button
            onClick={startNewChat}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm gap-2"
            size="sm"
            data-testid="new-chat-btn"
          >
            <Plus className="w-4 h-4" />
            {language === 'fr' ? 'Nouvelle conversation' : 'New conversation'}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="sessions-list">
          {sessionsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">
              {language === 'fr' ? 'Aucune conversation' : 'No conversations'}
            </p>
          ) : (
            sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => loadSession(s.session_id)}
                className={`w-full text-left rounded-lg px-3 py-2.5 group transition-colors ${
                  sessionId === s.session_id
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
                data-testid={`session-${s.session_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.title || (language === 'fr' ? 'Conversation' : 'Conversation')}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Clock className="w-3 h-3 text-slate-500 shrink-0" />
                      <span className="text-xs text-slate-500">{formatDate(s.last_message_at)}</span>
                      <span className="text-xs text-slate-600">·</span>
                      <span className="text-xs text-slate-500">{s.message_count} msg</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => deleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 transition-opacity"
                    data-testid={`delete-session-${s.session_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Main Chat */}
      <div className="flex-1 flex flex-col bg-[#E8ECF1]">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
          </button>
          <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-7 h-7 rounded-lg" data-testid="chat-logo" />
          <div>
            <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Poppins' }} data-testid="chat-title">
              {t('chat_title')}
            </h1>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                data-testid={`chat-message-${i}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  msg.role === 'user' ? 'bg-blue-100' : 'bg-slate-100'
                }`}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-blue-600" />
                  ) : (
                    <Bot className="w-4 h-4 text-slate-600" />
                  )}
                </div>
                <div className={`flex-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block max-w-[85%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-tr-sm'
                      : 'bg-white border border-slate-200 text-slate-700 rounded-tl-sm'
                  }`}>
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  </div>
                  {msg.role === 'assistant' && msg.id && msg.id !== 'welcome' && msg.id !== 'error' && (
                    <div className="mt-2">
                      {ratingMsg === msg.id ? (
                        <div className="flex items-center gap-1 justify-start" data-testid={`rating-stars-${i}`}>
                          {[1,2,3,4,5,6,7,8,9,10].map((r) => (
                            <button
                              key={r}
                              onClick={() => handleRate(msg.id, r)}
                              className="p-0.5 hover:scale-125 transition-transform"
                              data-testid={`rate-${r}`}
                            >
                              <Star className={`w-3.5 h-3.5 ${r <= 5 ? 'text-amber-400' : 'text-amber-300'}`} fill={r <= 5 ? 'currentColor' : 'none'} />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <button
                          onClick={() => setRatingMsg(msg.id)}
                          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                          data-testid={`rate-btn-${i}`}
                        >
                          <Star className="w-3 h-3" /> {t('chat_rate')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3" data-testid="chat-loading">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-slate-600" />
                </div>
                <div className="bg-white border border-slate-200 rounded-xl rounded-tl-sm px-4 py-3">
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="bg-white border-t border-slate-200 px-4 sm:px-6 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('chat_placeholder')}
                  className="w-full min-h-[44px] max-h-32 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={1}
                  data-testid="chat-input"
                />
              </div>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white h-11 w-11 p-0 rounded-xl shrink-0 active:scale-95 transition-transform"
                onClick={handleSend}
                disabled={!input.trim() || sending}
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2 text-center">
              {language === 'fr' ? 'Propulsé par Claude (Anthropic) - Les réponses peuvent contenir des erreurs.' : 'Powered by Claude (Anthropic) - Responses may contain errors.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
